import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, ExternalLink, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import bandeau from '../img/bandeau.jpg';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { newsArticles, categories } from '../data/newsData';

const NewsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredNews = newsArticles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         article.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const groupedNews = filteredNews.reduce((groups, article) => {
    const date = new Date(article.publishedDate);
    const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (!groups[monthYear]) {
      groups[monthYear] = [];
    }
    groups[monthYear].push(article);
    return groups;
  }, {});

  return (
    <div className="min-h-screen bg-white text-ninsun-text">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-40 pb-20 relative bg-ninsun-surface border-b border-gray-200 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={bandeau} 
            alt="Ninsun Biotech Background" 
            className="w-full h-full object-cover object-center opacity-60"
          />
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16 max-w-4xl mx-auto"
          >
            <Link 
              to="/"
              className="inline-flex items-center space-x-2 text-ninsun-gold hover:opacity-90 transition-opacity mb-8 font-bold font-mono text-sm tracking-widest uppercase"
            >
              <ArrowLeft size={16} />
              <span>Back to Home</span>
            </Link>
            <h1 className="text-5xl md:text-7xl font-display font-bold text-ninsun-text mb-6 tracking-tight">
              Latest <span className="text-gradient-gold">News</span>
            </h1>
            <p className="text-lg md:text-xl text-ninsun-muted leading-relaxed">
              Stay updated with the latest developments, research breakthroughs, and milestones from Ninsun Biotech.
            </p>
          </motion.div>

          {/* Search and Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="glass-panel p-6 rounded-2xl mb-12 max-w-4xl mx-auto"
          >
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-ninsun-muted w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search news..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-ninsun-text placeholder-ninsun-muted focus:ring-2 focus:ring-ninsun-gold focus:border-transparent outline-none transition-all"
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-6 py-3 bg-white border border-gray-200 rounded-lg text-ninsun-text focus:ring-2 focus:ring-ninsun-gold focus:border-transparent outline-none transition-all font-bold appearance-none cursor-pointer"
              >
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
          </motion.div>
        </div>
      </section>

      {/* News Content */}
      <section className="py-20 relative bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          {Object.keys(groupedNews).length === 0 ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <h3 className="text-2xl font-bold text-ninsun-text mb-4">No news found</h3>
              <p className="text-ninsun-muted">Try adjusting your search or category filter.</p>
            </motion.div>
          ) : (
            <div className="space-y-16">
              {Object.entries(groupedNews).map(([monthYear, articles], groupIndex) => (
                <motion.div 
                  key={monthYear}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <h2 className="text-2xl font-display font-bold text-ninsun-text mb-8 flex items-center gap-4">
                    <span>{monthYear}</span>
                    <div className="h-px bg-gray-200 flex-1"></div>
                  </h2>
                  <div className="grid gap-8">
                    {articles.map((article, index) => (
                      <motion.article 
                        key={article.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        viewport={{ once: true }}
                        className="glass-panel rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col md:flex-row group"
                      >
                        <div className="w-full md:w-1/3 relative overflow-hidden bg-gray-100 flex-shrink-0">
                          <img 
                            src={article.image} 
                            alt={article.title}
                            className="w-full h-full object-cover min-h-[250px] group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-8 md:p-10 flex flex-col justify-center flex-1">
                          <div className="flex items-center gap-4 mb-4">
                            <span className="px-3 py-1 bg-ninsun-gold/10 text-ninsun-gold font-bold text-sm rounded-full">
                              {article.category}
                            </span>
                            <div className="flex items-center text-ninsun-muted text-sm font-mono font-bold uppercase tracking-wider">
                              <Calendar size={16} className="mr-2" />
                              {new Date(article.publishedDate).toLocaleDateString()}
                            </div>
                          </div>
                          
                          <h3 className="text-2xl font-display font-bold text-ninsun-text mb-4 group-hover:text-ninsun-gold transition-colors line-clamp-2">
                            {article.title}
                          </h3>
                          
                          <p className="text-ninsun-muted mb-6 line-clamp-3 leading-relaxed">
                            {article.excerpt}
                          </p>

                          <div className="mt-auto">
                            {article.externalUrl ? (
                              <a 
                                href={article.externalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center space-x-2 text-ninsun-gold hover:opacity-90 font-bold transition-opacity"
                              >
                                <span>Read Full Article</span>
                                <ExternalLink size={18} />
                              </a>
                            ) : (
                              <Link 
                                to="/contact"
                                className="inline-flex items-center space-x-2 text-ninsun-gold hover:opacity-90 font-bold transition-opacity"
                              >
                                <span>Request E-Poster</span>
                                <ExternalLink size={18} />
                              </Link>
                            )}
                          </div>
                        </div>
                      </motion.article>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NewsPage;